/**
 * Section-level page patch computer.
 *
 * Given a prior HTML string and a new HTML string for the same page, produce
 * a sequence of patch messages that describe the section-level delta. The
 * messages are posted into the sandboxed iframe that renders the page, where
 * `inspector-script.ts` applies them as surgical DOM mutations instead of a
 * full srcDoc reparse.
 *
 * Why DOM-diff here (and not just reuse the agent's html.patch directive)?
 * The `html.patch` / `html.insert` / `html.remove` stream directives are
 * consumed by `useChat.ts` — which we do not touch in this commit — and
 * flattened into a cumulative HTML string on the `xavior:html-config-update`
 * event. By the time the patch info arrives at `PageRenderer`, the sectionId
 * has already been lost. Re-deriving per-section diffs from before/after
 * HTML keeps the parent-side change surface local to PageRenderer +
 * inspector-script while still delivering section-level patching.
 *
 * Any case the computer cannot express as section patches (root structure
 * changed, a patched section lacks `data-section-id`, section order changed
 * in a way that implies wholesale reorganization) falls through to a single
 * `{ type: 'xavior-full-reload' }` message — the parent then reassigns
 * srcDoc for that single update, which is the pre-commit-7 behavior and the
 * correct choice for a true rebuild.
 */
export type PatchMessage =
  | { type: 'xavior-patch-section'; sectionId: string; outerHTML: string }
  | { type: 'xavior-insert-section'; beforeSectionId: string | null; outerHTML: string; sectionId: string }
  | { type: 'xavior-remove-section'; sectionId: string }
  | { type: 'xavior-full-reload' };

interface SectionSnapshot {
  id: string;
  outerHTML: string;
}

/**
 * Append/patch root lookup. Pages may opt into a marked wrapper via
 * `[data-xavior-page-root]` (for layouts that have a non-body shell). If
 * absent, we fall back to document.body — the default authoring model.
 * Parent-side and iframe-side use the same rule so they stay symmetric.
 */
function getPatchRoot(doc: Document): Element | null {
  return doc.querySelector('[data-xavior-page-root]') || doc.body;
}

/**
 * Parse an HTML document string into a flat list of top-level
 * `[data-section-id]` sections in document order. We only consider sections
 * at the same depth level — nested sections are treated as part of their
 * parent's outerHTML. This matches the agent's authoring model (the SKILL
 * emits flat section arrays under a single `<body>`).
 */
function parseSections(html: string): SectionSnapshot[] | null {
  if (!html) return null;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Only direct children of the patch root count as a "section" for patch
    // purposes — avoids descending into markup where data-section-id may
    // appear inside a nested widget.
    const body = getPatchRoot(doc);
    if (!body) return null;
    const out: SectionSnapshot[] = [];
    for (const child of Array.from(body.children)) {
      const id = child.getAttribute('data-section-id');
      if (!id) {
        // A top-level child without a data-section-id means we cannot
        // represent the document as a flat section list; bail out.
        return null;
      }
      out.push({ id, outerHTML: child.outerHTML });
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Compute the patch sequence that transforms `priorHtml` into `newHtml`.
 *
 * Contract:
 *  - If either document cannot be parsed into a flat section list, returns
 *    `[{ type: 'xavior-full-reload' }]`.
 *  - If the `<head>` or document chrome differs, returns a single
 *    full-reload (we only patch body sections; html/head changes require
 *    a real reparse).
 *  - Otherwise emits per-section replace / insert / remove messages in
 *    the order: removes first (to free up the DOM), then inserts, then
 *    replaces. The iframe-side applier is independent per message, so
 *    ordering only matters for insert's `beforeSectionId` anchor.
 */
export function computePatches(priorHtml: string, newHtml: string): PatchMessage[] {
  if (!priorHtml) return [{ type: 'xavior-full-reload' }];
  if (priorHtml === newHtml) return [];

  const prior = parseSections(priorHtml);
  const next = parseSections(newHtml);
  if (!prior || !next) return [{ type: 'xavior-full-reload' }];

  // If the documents' <head> or <html> differ, patching body sections isn't
  // enough — the stylesheet / fonts / scripts may have changed. Fall back.
  if (!headMatches(priorHtml, newHtml)) return [{ type: 'xavior-full-reload' }];

  const priorById = new Map(prior.map((s) => [s.id, s]));
  const nextById = new Map(next.map((s) => [s.id, s]));

  const removes: PatchMessage[] = [];
  for (const s of prior) {
    if (!nextById.has(s.id)) removes.push({ type: 'xavior-remove-section', sectionId: s.id });
  }

  const inserts: PatchMessage[] = [];
  const replaces: PatchMessage[] = [];
  for (let i = 0; i < next.length; i++) {
    const s = next[i];
    const priorSnapshot = priorById.get(s.id);
    if (!priorSnapshot) {
      // Inserting — anchor on the next-known-prior sibling so the iframe
      // can `insertAdjacentHTML('beforebegin', ...)` it. If there is no
      // following section (append at end), beforeSectionId is null.
      let beforeSectionId: string | null = null;
      for (let j = i + 1; j < next.length; j++) {
        if (priorById.has(next[j].id)) {
          beforeSectionId = next[j].id;
          break;
        }
      }
      inserts.push({
        type: 'xavior-insert-section',
        beforeSectionId,
        outerHTML: s.outerHTML,
        sectionId: s.id,
      });
    } else if (priorSnapshot.outerHTML !== s.outerHTML) {
      replaces.push({ type: 'xavior-patch-section', sectionId: s.id, outerHTML: s.outerHTML });
    }
  }

  // If section order in `next` (considering only shared ids) differs from
  // `prior`, a straight replace/insert/remove sequence would leave sections
  // in the wrong order. Fall back to full reload — this is rare but real
  // (e.g. agent swaps "testimonials" with "features").
  if (!sharedOrderMatches(prior, next)) return [{ type: 'xavior-full-reload' }];

  return [...removes, ...inserts, ...replaces];
}

/**
 * Compare only the `<head>` segment of two documents. Differences in
 * `<style>`, `<link>`, `<script>`, `<meta>`, or font imports mean we can't
 * safely patch sections without risking visual inconsistency.
 */
function headMatches(a: string, b: string): boolean {
  const extract = (html: string): string => {
    const m = /<head\b[^>]*>([\s\S]*?)<\/head>/i.exec(html);
    return m ? m[1] : '';
  };
  return extract(a) === extract(b);
}

/**
 * Returns true if the order of section ids that appear in both prior and
 * next is the same in both. If prior has [a, b, c, d] and next has [a, c, d]
 * the shared sequence is [a, c, d] which matches prior's order — OK.
 */
function sharedOrderMatches(prior: SectionSnapshot[], next: SectionSnapshot[]): boolean {
  const nextIds = new Set(next.map((s) => s.id));
  const priorIds = new Set(prior.map((s) => s.id));
  const priorShared = prior.filter((s) => nextIds.has(s.id)).map((s) => s.id);
  const nextShared = next.filter((s) => priorIds.has(s.id)).map((s) => s.id);
  if (priorShared.length !== nextShared.length) return false;
  for (let i = 0; i < priorShared.length; i++) {
    if (priorShared[i] !== nextShared[i]) return false;
  }
  return true;
}

